import classNames from 'clsx';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import StarsButton from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { Site } from '@swf/core/enums';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoTable } from '@swf/core/types/CasinoGame';

import FeatureDetails, { FormatType } from '../FeatureDetails/FeatureDetails';

import {
  TXTACL_Bonus_Features,
  TXTACL_Maximum_Bet,
  TXTACL_Minimum_Bet,
  TXTACL_Paylines,
  TXTACL_Return_to_Player,
  TXTCLI_Play,
  TXTWCL_Bonus_Round,
  TXTWCL_Features,
  TXTWCL_Free_Spins,
  TXTWCL_Jackpot,
  TXTWCL_Join
} from './GameFeatures.messages.json';
import styles from './GameFeatures.scss';

export enum ResolutionView {
  Phone = 'phoneView',
  Tablet = 'desktopView'
}

export interface Props {
  data: CasinoTable;
  view: ResolutionView;
  gameName: string;
  fsbLaunch?: boolean;
  raceId?: string;
  tags?: string[];
}

const bonusFeatures = {
  pgjkpt: TXTWCL_Jackpot,
  bnsRnd: TXTWCL_Bonus_Round,
  fsbTitle: TXTWCL_Free_Spins
};

const paylines = {
  Megaways: 'Megaways™',
  ClusterPays: 'Cluster Pays'
};

const getPayline = (value: string, tags: string[] = []) =>
  Object.entries(paylines).reduce(
    (acc: string, [key, entryValue]) => (tags.indexOf(key) !== -1 ? entryValue : acc),
    value
  );

const GameFeatures: React.FC<Props> = (props) => {
  const { data, view, gameName, fsbLaunch, raceId, tags } = props;
  const siteId = useSelector(select.site.getId);
  const {
    casino: { launch }
  } = useDispatch<Dispatch>();
  const { modal } = useDispatch<Dispatch>();
  const { minBet, maxBet, featureNames, properties, id, playMoney, currency } = data;

  const features = [
    {
      title: TXTACL_Minimum_Bet,
      value: minBet,
      format: FormatType.Currency
    },
    {
      title: TXTACL_Maximum_Bet,
      value: maxBet,
      format: FormatType.Currency
    },
    {
      title: TXTACL_Return_to_Player,
      value: properties?.holdRate ? 100 - properties.holdRate / 1e5 : 0,
      format: FormatType.Percentage
    },
    {
      title: TXTACL_Paylines,
      value: getPayline(properties?.lines || '', tags),
      format: FormatType.String
    },
    {
      title: TXTACL_Bonus_Features,
      value: featureNames?.length ? featureNames.map((feature) => bonusFeatures[feature]) : 0,
      format: FormatType.Translation
    }
  ]
    .filter(({ value }) => !!value)
    .map((feature, index) => <FeatureDetails key={index} {...feature} currency={currency} />);

  const onPlayClickHandler = () => {
    modal.close('/casino/game-info');
    launch({ table: { id, playMoney } as CasinoTable, fsbLaunch, raceId });
  };

  return (
    <div className={classNames(styles.gameInfo, styles[view])}>
      <h1 className={styles.gameTitle}>{gameName}</h1>
      <div>
        <StarsButton
          className={styles.gameButton}
          onClick={() => onPlayClickHandler()}
          translation={siteId === Site.GR ? TXTWCL_Join : TXTCLI_Play}
        />
        <span className={styles.gameInfoSh}>
          <Translation {...TXTWCL_Features} />
        </span>
        {features}
      </div>
    </div>
  );
};

export default GameFeatures;
